import {
  Button,
  Checkbox,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { formatDateTime } from "../ai-reports/status";
import ConversationModal from "./ConversationModal";
import { CONVERSATION_STATUS, StatusTag, formatUsd } from "./labels";

const { Text } = Typography;
const POR_PAGINA = 50;

export default function ConversationsCard({ filtros, usuarios }) {
  const { getAgentConversations } = useApi();
  const [userId, setUserId] = useState(undefined);
  const [texto, setTexto] = useState("");
  const [busca, setBusca] = useState("");
  const [comProblema, setComProblema] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [aberta, setAberta] = useState(null);

  useEffect(() => {
    setPagina(1);
  }, [filtros.inicio, filtros.fim, filtros.client_id, filtros.branch_id]);

  useEffect(() => {
    if (userId !== undefined && !usuarios.some((u) => u.id === userId)) {
      setUserId(undefined);
    }
  }, [usuarios, userId]);

  const params = {
    inicio: filtros.inicio,
    fim: filtros.fim,
    client_id: filtros.client_id,
    branch_id: filtros.branch_id,
    user_id: userId,
    texto: busca || undefined,
    com_problema: comProblema ? "true" : undefined,
    limit: POR_PAGINA,
    offset: (pagina - 1) * POR_PAGINA,
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["agent-conversations", params],
    queryFn: () => getAgentConversations(params),
    staleTime: 0,
  });

  const buscar = () => {
    setPagina(1);
    setBusca(texto.trim());
  };

  const colunas = [
    {
      title: "Última mensagem",
      dataIndex: "last_message_at",
      key: "last",
      width: 150,
      render: formatDateTime,
    },
    { title: "Cliente", dataIndex: "cliente", key: "cliente" },
    {
      title: "Filial",
      dataIndex: "filiais",
      key: "filial",
      render: (filiais, r) =>
        (filiais || []).length ? (
          filiais.map((filial) => <Tag key={filial.id}>{filial.nome}</Tag>)
        ) : (
          <Text type="secondary">
            {r.canal === "whatsapp" ? "nenhuma ferramenta rodou" : r.filial}
          </Text>
        ),
    },
    {
      title: "Usuário",
      dataIndex: "usuario",
      key: "usuario",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Mensagens",
      dataIndex: "mensagens",
      key: "mensagens",
      align: "right",
      width: 100,
    },
    {
      title: "Problemas",
      dataIndex: "problemas",
      key: "problemas",
      align: "right",
      width: 100,
      render: (v) => (v ? <Tag color="error">{v}</Tag> : 0),
    },
    {
      title: (
        <Tooltip title="Alterações confirmadas e gravadas nesta conversa">
          Alterações
        </Tooltip>
      ),
      dataIndex: "acoes_executadas",
      key: "acoes",
      align: "right",
      width: 80,
    },
    {
      title: "Custo",
      dataIndex: "custo_usd",
      key: "custo",
      align: "right",
      width: 110,
      render: (v) => formatUsd(v),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <StatusTag status={s} mapa={CONVERSATION_STATUS} />,
    },
    {
      title: "",
      key: "ver",
      width: 70,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => setAberta(r.id)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Usuário"
          style={{ width: 260 }}
          value={userId}
          options={usuarios.map((u) => ({ value: u.id, label: u.email }))}
          onChange={(v) => {
            setPagina(1);
            setUserId(v ?? undefined);
          }}
        />
        <Input
          allowClear
          placeholder="Texto nas mensagens"
          style={{ width: 240 }}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            if (!e.target.value) {
              setPagina(1);
              setBusca("");
            }
          }}
          onPressEnter={buscar}
        />
        <Button onClick={buscar}>Buscar</Button>
        <Checkbox
          checked={comProblema}
          onChange={(e) => {
            setPagina(1);
            setComProblema(e.target.checked);
          }}>
          Só com problema
        </Checkbox>
        <Button onClick={() => refetch()} loading={isFetching}>
          Atualizar
        </Button>
      </Space>
      <Table
        dataSource={data?.conversas || []}
        columns={colunas}
        rowKey="id"
        size="middle"
        loading={isFetching}
        onRow={(r) => ({ onDoubleClick: () => setAberta(r.id) })}
        pagination={{
          current: pagina,
          pageSize: POR_PAGINA,
          total: data?.total || 0,
          showSizeChanger: false,
          showTotal: (total) => `${total} conversa(s)`,
          onChange: setPagina,
        }}
        locale={{ emptyText: "Nenhuma conversa no período." }}
      />
      <ConversationModal
        conversationId={aberta}
        onClose={() => setAberta(null)}
      />
    </div>
  );
}
